import { GetQuoteResponse } from 'src/model/api/quote/get-quote-response.model';
import { GetRoutingLinesResponse } from 'src/model/api/get-routing-lines-response.model';
import { InstallationCablingResponse } from "src/model/api/v2/data-contracts";
import {
  GroupedElement,
  GroupNameByUgroupIdRequest,
  GroupNameByUgroupIdResponse
} from "src/model/api/grouped-elements.model";
import { NHCPinoutDevices } from "src/model/api/nhc-pinout-devices";
import { CableListDevice } from "src/model/api/cable-list-device.model";
import { QuoteData } from "src/model/api/quote-data.model";
import { ServiceDiscount } from "src/model/api/manufacturer-discount.model";
import { LegacyAPIResponse } from 'src/model/api/legacy-api-response.model';
import { GetExchangeRatesResponse } from "src/model/api/get-exchange-rates.model";
import { supportedCurrencies } from "src/utils/controllerAdapterForOfferPage";
import { Product } from "src/model/product.model";
import { GetQuoteCustomItemRecommendationRequest } from "src/model/api/get-quote-custom-item-recommendation.model";
import {
  AddCustomItemWithProjectIdRequest,
  AddCustomItemWithRoomIdRequest,
  DeleteConvFunctionCustomItemRequest,
  DeleteFunctionItemRequest,
  DeleteNhcDinCustomItemRequest,
  DeleteProjectCableCustomItemRequest,
  DeleteProjectCustomItemRequest,
  DeleteRackCustomItemRequest,
  DeleteRoomCustomItemRequest,
  UpdateConvFunctionCustomItemRequest,
  UpdateFunctionItemRequest,
  UpdateNhcDinCustomItemRequest,
  UpdateProjectCableCustomItemRequest,
  UpdateProjectCustomItemRequest,
  UpdateRackCustomItemRequest,
  UpdateRoomCustomItemRequest
} from "src/model/api/custom-item-request.model";
import { TAG_GET_QUOTE, TAG_PINOUT_DEVICES, TAG_PROJECT_QUOTE_DATA } from "src/api/api-tags";
import { appApiSlice } from "src/api/app-ApiSlice";


export const quoteApiSlice = appApiSlice.injectEndpoints({
  endpoints: builder => ({
    getQuoteData: builder.query<QuoteData, number | void>({
      query(projectId: number) {
        return `v1/quote/get-quote-data?project_id=${projectId}`;
      },
      providesTags: [{ type: TAG_PROJECT_QUOTE_DATA }]
    }),
    getQuote: builder.query<GetQuoteResponse, number | void>({
      query(projectId: number) {
        const dummyRoomOptions = [{
          "room_id": 0,
          "manufacturer_id": 0,
          "color_id": 0
        }]
        return {
          url: `v1/quote/get-quote`,
          method: 'POST',
          body: {
            project_id: projectId,
            room_options: JSON.stringify(dummyRoomOptions)
          }
        };
      },
      providesTags: (result: GetQuoteResponse | void, error) => [
        { type: TAG_GET_QUOTE }
      ]
    }),
    getRoutingLines: builder.query<GetRoutingLinesResponse, number | void>({
      query(projectId: number) {

        return {
          url: `v1/quote/routing-lines`,
          method: 'POST',
          body: {
            project_id: projectId,
          },
        };
      },
      transformResponse: (response: string) => {
        return JSON.parse(response) as GetRoutingLinesResponse;
      },
      providesTags: [{ type: TAG_PROJECT_QUOTE_DATA }]
    }),
    installationCabling: builder.query<InstallationCablingResponse, number>({
      query(projectId: number) {
        return `v2/quote/installation-cabling/${projectId}`
      },
      providesTags: [{ type: TAG_PROJECT_QUOTE_DATA }]
    }),
    getGroupedElements: builder.query<GroupedElement[], number>({
      query(projectId: number) {
        return `v1/quote/get-grouped-elements?project_id=${projectId}`
      },
      providesTags: (result, error, projectId) => [
        { type: TAG_PINOUT_DEVICES, id: projectId!! }
      ]
    }),
    saveGroupNameByUgroupId: builder.mutation<GroupNameByUgroupIdResponse, GroupNameByUgroupIdRequest>({
      query(arg: GroupNameByUgroupIdRequest) {
        return {
          url: 'v1/quote/save-group-name-by-ugroup-id',
          method: 'POST',
          body: arg
        }
      },
      invalidatesTags: (result: GroupNameByUgroupIdResponse | void, error, req: GroupNameByUgroupIdRequest) => [
        { type: TAG_PINOUT_DEVICES, id: req.project_id }
      ]
    }),
    getNHCPinoutDevices: builder.query<NHCPinoutDevices, number>({
      query(projectId: number) {
        return `v1/quote/get-nhc-pinout-devices?project_id=${projectId}`
      },
      providesTags: (result: NHCPinoutDevices | void, error, projectId: number) => [
        { type: TAG_PINOUT_DEVICES, id: projectId!! }
      ]
    }),
    cableList: builder.query<CableListDevice[], number>({
      query(projectId: number) {
        return {
          url: `v1/quote/cable-list`,
          method: 'POST',
          body: {
            project_id: projectId
          }
        }
      },
      providesTags: [{ type: TAG_PROJECT_QUOTE_DATA }]
    }),
    updateServiceDiscount: builder.mutation<LegacyAPIResponse, ServiceDiscount>({
      query(discountData: ServiceDiscount) {
        return {
          url: 'v1/quote/update-service-discount',
          method: 'PUT',
          body: discountData
        }
      }
    }),
    getExchangeRates: builder.query<GetExchangeRatesResponse, void>({
      query() {
        return `v1/quote/exchange-rates`
      },
      transformResponse(response: any) {
        const othersThanHuf = supportedCurrencies.filter(function (curr) { return curr !== "huf"; });
        let exchangeRates: any = {};
        for (let currency of supportedCurrencies) {
          exchangeRates[currency] = {};
        }
        for (let currency of othersThanHuf) {
          const currHuf = +(response[currency + "-huf"]);
          exchangeRates[currency]['huf'] = currHuf;
          exchangeRates['huf'][currency] = 1 / currHuf;
        }
        for (let p1 = 0; p1 < othersThanHuf.length - 1; p1++) {
          for (let p2 = p1 + 1; p2 < othersThanHuf.length; p2++) {
            const curr1 = othersThanHuf[p1];
            const curr2 = othersThanHuf[p2];
            const curr1Huf = exchangeRates[curr1]['huf'];
            const curr2Huf = exchangeRates[curr2]['huf'];
            exchangeRates[curr1][curr2] = curr1Huf / curr2Huf;
            exchangeRates[curr2][curr1] = curr2Huf / curr1Huf;
          }
        }
        return exchangeRates;
      }
    }),
    getQuoteCustomItemRecommendation: builder.query<Product[], GetQuoteCustomItemRecommendationRequest>({
      query(request: GetQuoteCustomItemRecommendationRequest) {
        return {
          url: `v1/quote/get-quote-custom-item-recommendation?search_string=${request.search_string}&project_id=${request.project_id}&get_all=${request.get_all}`
        }
      }
    }),
    addRoomCustomItem: builder.mutation<any, AddCustomItemWithRoomIdRequest>({
      query(customItemData: AddCustomItemWithRoomIdRequest) {
        return {
          url: `v1/quote/room-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithRoomIdRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateRoomCustomItem: builder.mutation<any, UpdateRoomCustomItemRequest>({
      query(customItemData: UpdateRoomCustomItemRequest) {
        return {
          url: `v1/quote/room-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateRoomCustomItemRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteRoomCustomItem: builder.mutation<any, DeleteRoomCustomItemRequest>({
      query(customItemData: DeleteRoomCustomItemRequest) {
        return {
          url: `v1/quote/room-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteRoomCustomItemRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addProjectCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/project-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithProjectIdRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateProjectCustomItem: builder.mutation<any, UpdateProjectCustomItemRequest>({
      query(customItemData: UpdateProjectCustomItemRequest) {
        return {
          url: `v1/quote/project-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateProjectCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteProjectCustomItem: builder.mutation<any, DeleteProjectCustomItemRequest>({
      query(customItemData: DeleteProjectCustomItemRequest) {
        return {
          url: `v1/quote/project-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteProjectCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addElectricalCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/electrical-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithProjectIdRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateElectricalCustomItem: builder.mutation<any, UpdateFunctionItemRequest>({
      query(customItemData: UpdateFunctionItemRequest) {
        return {
          url: `v1/quote/electrical-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateFunctionItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteElectricalCustomItem: builder.mutation<any, DeleteFunctionItemRequest>({
      query(customItemData: DeleteFunctionItemRequest) {
        return {
          url: `v1/quote/electrical-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteFunctionItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addNhcCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/nhc-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req:AddCustomItemWithProjectIdRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateNhcCustomItem: builder.mutation<any, UpdateNhcDinCustomItemRequest>({
      query(customItemData: UpdateNhcDinCustomItemRequest) {
        return {
          url: `v1/quote/nhc-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateNhcDinCustomItemRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteNhcCustomItem: builder.mutation<any, DeleteNhcDinCustomItemRequest>({
      query(customItemData: DeleteNhcDinCustomItemRequest) {
        return {
          url: `v1/quote/nhc-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteNhcDinCustomItemRequest )=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addRackCabinetCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/rack-cabinet-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithProjectIdRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateRackCabinetCustomItem: builder.mutation<any, UpdateRackCustomItemRequest>({
      query(customItemData: UpdateRackCustomItemRequest) {
        return {
          url: `v1/quote/rack-cabinet-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateRackCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteRackCabinetCustomItem: builder.mutation<any, DeleteRackCustomItemRequest>({
      query(customItemData: DeleteRackCustomItemRequest) {
        return {
          url: `v1/quote/rack-cabinet-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteRackCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addCableCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/cable-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithProjectIdRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateCableCustomItem: builder.mutation<any, UpdateProjectCableCustomItemRequest>({
      query(customItemData: UpdateProjectCableCustomItemRequest) {
        return {
          url: `v1/quote/cable-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateProjectCableCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteCableCustomItem: builder.mutation<any, DeleteProjectCableCustomItemRequest>({
      query(customItemData: DeleteProjectCableCustomItemRequest) {
        return {
          url: `v1/quote/cable-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteProjectCableCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    addConventionalDevicesCustomItem: builder.mutation<any, AddCustomItemWithProjectIdRequest>({
      query(customItemData: AddCustomItemWithProjectIdRequest) {
        return {
          url: `v1/quote/conventional-devices-custom-item`,
          method: 'POST',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: AddCustomItemWithProjectIdRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    updateConventionalDevicesCustomItem: builder.mutation<any, UpdateConvFunctionCustomItemRequest>({
      query(customItemData: UpdateConvFunctionCustomItemRequest) {
        return {
          url: `v1/quote/conventional-devices-custom-item`,
          method: 'PUT',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: UpdateConvFunctionCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    deleteConventionalDevicesCustomItem: builder.mutation<any, DeleteConvFunctionCustomItemRequest>({
      query(customItemData: DeleteConvFunctionCustomItemRequest) {
        return {
          url: `v1/quote/conventional-devices-custom-item`,
          method: 'DELETE',
          body: customItemData,
        }
      },
      invalidatesTags: (result: void, error, req: DeleteConvFunctionCustomItemRequest)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
    resetCustomItem: builder.mutation<any, number>({
      query(customItemId: number) {
        return {
          url: `v1/quote/reset-custom-item`,
          method: 'PUT',
          body: {
            customItemId: customItemId,
          }
        }
      },
      invalidatesTags: (result: void, error, req: number)=> [
        { type: TAG_GET_QUOTE }
      ]
    }),
  })
});

export const {
  useGetQuoteDataQuery,
  useGetQuoteQuery,
  useGetRoutingLinesQuery,
  useInstallationCablingQuery,
  useGetGroupedElementsQuery,
  useSaveGroupNameByUgroupIdMutation,
  useGetNHCPinoutDevicesQuery,
  useCableListQuery,
  useUpdateServiceDiscountMutation,
  useGetQuoteCustomItemRecommendationQuery,
  useAddRoomCustomItemMutation,
  useUpdateRoomCustomItemMutation,
  useDeleteRoomCustomItemMutation,
  useAddProjectCustomItemMutation,
  useUpdateProjectCustomItemMutation,
  useDeleteProjectCustomItemMutation,
  useAddElectricalCustomItemMutation,
  useUpdateElectricalCustomItemMutation,
  useDeleteElectricalCustomItemMutation,
  useAddNhcCustomItemMutation,
  useUpdateNhcCustomItemMutation,
  useDeleteNhcCustomItemMutation,
  useAddRackCabinetCustomItemMutation,
  useUpdateRackCabinetCustomItemMutation,
  useDeleteRackCabinetCustomItemMutation,
  useAddCableCustomItemMutation,
  useUpdateCableCustomItemMutation,
  useDeleteCableCustomItemMutation,
  useAddConventionalDevicesCustomItemMutation,
  useUpdateConventionalDevicesCustomItemMutation,
  useDeleteConventionalDevicesCustomItemMutation,
  useResetCustomItemMutation,
  useGetExchangeRatesQuery
} = quoteApiSlice;